'use client';

import { useCallback } from 'react';
import { addHours, getUnixTime, subHours } from 'date-fns';
import { UAParser } from 'ua-parser-js';
import { CONFIG } from '@frameio/config';
import { getDatadogRumSessionUrl } from '@frameio/telemetry/datadog';
import { auth } from '@frameio/utils/auth';
import { useIsAppleOs } from '@frameio/vapor';
import { useActiveAccountId } from '../contexts';
import type { DevFlag } from '../dev-flags';
import { AvailableDevFlags, getDevFlags } from '../dev-flags';
import { useCurrentPlanTracking } from '../plans/hooks/useCurrentPlanTracking';
import { useMeCachedQuery } from './useMeCachedQuery';

type JiraArgs = {
  buildInfoParams?: Record<string, string>;
  jiraProjectId?: string;
  titleOverride?: string;
};

/**
 * Opens a jira ticket when the users presses Command-Backslash (or on Windows,
 * Ctrl-Backslash). For jira wiki-syntax documentation please refer to these
 * links:
 *
 * - https://confluence.atlassian.com/jirakb/how-to-create-issues-using-direct-html-links-in-jira-server-159474.html
 * - https://jira.atlassian.com/secure/WikiRendererHelpAction.jspa?section=all
 */
export function useQuickBugReportTrigger() {
  const activeAccountId = useActiveAccountId();
  const currentPlanName = useCurrentPlanTracking()?.current_plan_name;
  const isAppleOs = useIsAppleOs();
  const devFlags = getDevFlags();
  const me = useMeCachedQuery()?.data?.me;
  const userEmail = me?.email;
  const userName = me?.name;
  const userTimezone = me && 'timezone' in me && me.timezone;
  const isAnonymous = auth.getUserId() === 'anonymous user';

  const getJiraIssueTemplateUrl = useCallback(
    (opts: JiraArgs = {}) => {
      const parsedUA = new UAParser().getResult();

      const template = [
        'EXPECTED - Add information on what you thought should happen',
        'ACTUAL -   What did happen',
        'STEPS -    How did you get this to happen',
        'NOTES -    Any additional useful information',
      ].join('\n');

      const buildInfoParams = {
        URL: window?.location.href,
        Environment: CONFIG.name?.split('_')?.[0],
        Version: CONFIG.releaseVersion?.startsWith('v')
          ? `[${CONFIG.releaseVersion}|https://github.com/Frameio/web-app/releases/tag/${CONFIG.releaseVersion}]`
          : CONFIG.releaseVersion,
        'Account Plan': currentPlanName,
        User: userName
          ? `${userName} <${userEmail}> (${userTimezone})`
          : 'unknown',
        Browser: `${parsedUA.browser.name} ${parsedUA.browser.version}, ${
          parsedUA.os.name
        } ${
          parsedUA.os.version
        } ([detected|https://whatsmyua.info?ua=${encodeURIComponent(
          navigator?.userAgent
        )}|${navigator?.userAgent}])`,
        Tools: [
          [
            'support-tool user',
            isAnonymous
              ? undefined
              : `${CONFIG.supportToolOrigin}/users/${auth.getUserId()}`,
          ],
          [
            'support-tool account',
            isAnonymous || !activeAccountId
              ? undefined
              : `${CONFIG.supportToolOrigin}/items?account_id=${activeAccountId}`,
          ],
          ['datadog', isAnonymous ? undefined : getDatadogRumSessionUrl()],
          [
            'mux',
            isAnonymous
              ? undefined
              : `https://dashboard.mux.com/organizations/gd7rg3/environments/phgqe5/views?filters%5B0%5D=viewer_user_id%3a${auth.getUserId()}&timeframe%5B0%5D=${getUnixTime(
                  subHours(new Date(), 1)
                )}&timeframe%5B1%5D=${getUnixTime(addHours(new Date(), 1))}`,
          ],
        ]
          .filter(([__title, url]) => url)
          .map(([title, url]) => `[${title}|${url}]`)
          .join(', '),
        'Dev Flags Enabled': [...devFlags]
          .sort()
          .map((flagName) => `${'{{'}${flagName}${'}}'}`)
          .join(', '),
        'Dev Flags Disabled': devFlags?.length
          ? Object.keys(AvailableDevFlags)
              .filter((flagName) => !devFlags.includes(flagName as DevFlag))
              .sort()
              .map((flagName) => `${'{{'}${flagName}${'}}'}`)
              .join(', ')
          : undefined,
        ...opts.buildInfoParams,
      };

      const buildInfo = Object.entries(buildInfoParams)
        .filter(([__key, param]) => param)
        .map(([key, param]) => `*${key}:* ${param}`)
        .join(' \\\\');

      const searchParams = new URLSearchParams();
      searchParams.set('pid', opts.jiraProjectId || '10121'); // "V4BG" project id
      searchParams.set('priority', '3'); // "P2" priority id
      searchParams.set('issuetype', '10004'); // "Bug" type id
      searchParams.set('customfield_10184', '10523'); // "Not Sure Which Squad Should Own This"
      searchParams.set('reporter', userEmail?.split('@')[0]);
      searchParams.set(
        'summary',
        opts.titleOverride || 'TYPE ISSUE TITLE HERE'
      );
      searchParams.set(
        'description',
        `${template}\n\n\n{panel:bgColor=#f3f3f7}${buildInfo}{panel}`
      );

      return `https://frame-io.atlassian.net/secure/CreateIssueDetails!init.jspa?${searchParams.toString()}`;
    },
    [
      activeAccountId,
      currentPlanName,
      devFlags,
      isAnonymous,
      userEmail,
      userName,
      userTimezone,
    ]
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const modKey = isAppleOs ? event.metaKey : event.ctrlKey;
      if (modKey && event.key === '\\') {
        const jiraIssueTemplateUrl = getJiraIssueTemplateUrl();
        window.open(jiraIssueTemplateUrl);
      }
    },
    [getJiraIssueTemplateUrl, isAppleOs]
  );

  return {
    getJiraIssueTemplateUrl,
    onKeyDown,
  };
}
